import { useCallback, useEffect, useMemo } from "react";

import clone from "lodash/clone";

import { useTierPricingContext } from "./TierPricingProvider";

const testMaxTierData = ({ numberOfSlots }) => numberOfSlots === null;

const sortByNumberOfSlots = (tierData) =>
  tierData?.sort(({ numberOfSlots: a }, { numberOfSlots: b }) => {
    if (a === null) return 1;
    else if (b === null) return -1;
    else return a - b;
  });

const useSortedTierData = () => {
  const { tierData } = useTierPricingContext();
  const sortedTierData = clone(tierData);

  return sortByNumberOfSlots(sortedTierData);
};

const useNonMaxTierData = () => {
  const sortedTierData = useSortedTierData();
  const nonMaxTierData = sortedTierData.filter(
    (elem) => !testMaxTierData(elem)
  );
  return nonMaxTierData;
};

const useMaxCostHeaderText = () => {
  const { tierData } = useTierPricingContext();
  const nonMaxTierData = useNonMaxTierData({ tierData });

  if (nonMaxTierData.length === 0) return null;

  return nonMaxTierData[nonMaxTierData.length - 1]?.numberOfSlots + 1;
};

const useMinTierCost = () => {
  const tierData = useSortedTierData();
  return tierData?.[0]?.amount || 0;
};

const useMaxTierId = () => {
  const { tierData } = useTierPricingContext();
  return useMemo(() => {
    const maxPrice = tierData.find((tier) => tier.numberOfSlots === null);
    return maxPrice?.id || maxPrice?.ID;
  }, [tierData]);
};

const useMaxTierCost = () => {
  const { tierData, setTierData } = useTierPricingContext();

  const maxTierId = useMaxTierId();

  const setTier = useSetTier();
  const setMaxTierCost = (newMax) => {
    setTier({ newAmount: newMax, id: maxTierId });
  };

  // If there is no max tier, then make one. The ID is needed here to make a key so that the component remounts when it changes from outside the module.
  useEffect(() => {
    if (!tierData || tierData.length === 0) {
      setTierData([
        {
          amount: "5.00",
          numberOfSlots: null,
          id: Date.now(),
        },
      ]);
    }
  }, [tierData, setTierData]);

  const maxTierCost = useMemo(
    () => tierData.find(testMaxTierData)?.amount || "",
    [tierData]
  );

  return [maxTierCost, setMaxTierCost];
};

const increment = (numberToIncrement) => {
  const newNumber = Math.ceil(Number(numberToIncrement || 0) * 1.25);
  return Math.max(newNumber, 2, Number(numberToIncrement || 0) + 2);
};

const useAddTier = () => {
  const { setTierData, tierData, setErrorMessage } = useTierPricingContext();
  const nonMaxTierData = useNonMaxTierData();

  const addTier = (newNumberOfSlots) => {
    setErrorMessage(false);

    const { numberOfSlots } = nonMaxTierData.slice(-1)[0] || {};

    const defaultSlots = newNumberOfSlots || increment(numberOfSlots);

    if (tierData.length === 0) {
      tierData.push({
        numberOfSlots: null,
        amount: null,
      });
    }

    const newTierData = [
      ...tierData,
      {
        numberOfSlots: defaultSlots,
        amount: null,
        isNew: true,
        id: Date.now(),
      },
    ];

    const sortedData = sortByNumberOfSlots(newTierData);

    let previousPrice = 0;

    for (const tier of sortedData) {
      if (tier.amount) {
        if (tier.amount * 1 < previousPrice + 2) {
          tier.amount = previousPrice + 2;
          tier.shouldFlashAmount = true;
        }
        previousPrice = tier.amount * 1;

        tier.amount = (Math.round(tier.amount * 100) / 100).toFixed(2);
      } else previousPrice = previousPrice + 2;
    }

    setTierData(sortedData);
  };

  return addTier;
};

const useSuggestedDonation = () => {
  const { suggestedDonation } = useTierPricingContext();

  return suggestedDonation;
};

const useEntity = () => {
  const { entity } = useTierPricingContext();

  return entity;
};

const useIsDisabled = () => {
  const { disabled } = useTierPricingContext();

  return disabled;
};

const errorMessages = {
  difference:
    "There must be at least a $2.00 difference between each tier level.",
  atLeast: "The price must be at least $2.00.",
  atMost: "The price must be $10,000 or less.",
};

errorMessages.differenceAtLeast = `${errorMessages.difference} ${errorMessages.atLeast}`;
errorMessages.differenceAtMost = `${errorMessages.difference} ${errorMessages.atMost}`;

const minAllowedPrice = 2;
const maxAllowedPrice = 10000;

const useSetTier = () => {
  const {
    setTierData,
    tierData,
    setErrorMessage,
    errorMessage,
    setTierDataValid,
  } = useTierPricingContext();

  useEffect(
    /*
         This is necessary to ensure the disabled state does not "wait" for setTier()^ calls
         (from amount <Input>s being "blurred" etc.), which is particularly noticeable when toggling
         the "One Price ..." vs "Use Tiered Pricing" tabs
    
         This ultimately could just be a `const isTierDataValid = ...` and exported, but that would require several
         other changes (including changing stories and tests)
         */
    () => {
      const allHaveValue =
        tierData.length > 0 &&
        !tierData.some((tier) => !tier.amount || +tier.amount === 0);
      setTierDataValid(allHaveValue);
    },
    [errorMessage, setTierDataValid, tierData]
  );

  const setTier = useCallback(
    ({ newAmount, newNumberOfSlots, id }) => {
      let newErrorMessage = "";
      let newTierData = clone(tierData);
      if (!id && !newNumberOfSlots && newAmount && tierData.length === 0) {
        newTierData = [
          {
            numberOfSlots: null,
            amount: newAmount,
          },
        ];
      }

      const currentTier = newTierData.find((tier) => {
        // depending on where the tier data is coming from, it may had an id or an ID;
        const keys = Object.keys(tier);
        const has_ID = keys.includes("ID");

        if (has_ID) return tier.ID === id;
        return tier.id === id;
      });

      const isDecrementingSlots =
        newNumberOfSlots && newNumberOfSlots < currentTier.numberOfSlots;
      const isDecrementingPrice =
        newAmount && newAmount * 1 < currentTier.amount * 1;

      if (currentTier) {
        if (newAmount !== undefined) currentTier.amount = newAmount;
        if (newNumberOfSlots !== undefined)
          currentTier.numberOfSlots = newNumberOfSlots;
      }

      if (newNumberOfSlots) {
        if (isDecrementingSlots) {
          newTierData.reverse();

          let previousDecrementSlots;

          for (const tier of newTierData) {
            if (tier.id && tier.numberOfSlots) {
              if (previousDecrementSlots) {
                if (tier.numberOfSlots * 1 > previousDecrementSlots - 1) {
                  tier.numberOfSlots = previousDecrementSlots - 1;
                  tier.shouldFlashSlots = tier.shouldFlashSlots
                    ? tier.shouldFlashSlots + 1
                    : 1;
                }
              }

              previousDecrementSlots = tier.numberOfSlots * 1;
            }
          }
          newTierData.reverse();
        } else {
          let previousSlots = 0;

          for (const tier of newTierData) {
            if (
              tier.id &&
              tier.numberOfSlots &&
              tier.numberOfSlots * 1 < previousSlots + 1
            ) {
              tier.numberOfSlots = previousSlots + 1;
              tier.shouldFlashSlots = tier.shouldFlashSlots
                ? tier.shouldFlashSlots + 1
                : 1;
            }
            previousSlots = tier.numberOfSlots * 1;
          }
        }
      }

      if (newAmount) {
        for (const tier of newTierData) {
          if (tier.amount * 1 < minAllowedPrice) {
            tier.amount = minAllowedPrice;
            tier.shouldFlashAmount = tier.shouldFlashAmount
              ? tier.shouldFlashAmount + 1
              : 1;
            newErrorMessage = errorMessages.atLeast;
          }

          if (tier.amount * 1 > maxAllowedPrice) {
            tier.amount = maxAllowedPrice;
            tier.shouldFlashAmount = tier.shouldFlashAmount
              ? tier.shouldFlashAmount + 1
              : 1;
            newErrorMessage = errorMessages.atMost;
          }
        }

        if (isDecrementingPrice) {
          newTierData.reverse();

          let previousDecrementPrice;
          let minPrice = newTierData.length * 2;

          for (const tier of newTierData) {
            if (tier.amount) {
              if (previousDecrementPrice) {
                if (tier.amount * 1 > previousDecrementPrice - 2) {
                  tier.amount = previousDecrementPrice - 2;
                  tier.shouldFlashAmount = tier.shouldFlashAmount
                    ? tier.shouldFlashAmount + 1
                    : 1;
                  newErrorMessage = errorMessages.difference;
                }
              }
              tier.amount = (Math.round(tier.amount * 100) / 100).toFixed(2);

              previousDecrementPrice = tier.amount * 1;
              if (tier.amount < minAllowedPrice) {
                setErrorMessage(errorMessages.differenceAtLeast);
                return; // abort the opperation, it is not possible to make it valid
              }
            } else {
              if (previousDecrementPrice)
                previousDecrementPrice = previousDecrementPrice - 2;
            }
            minPrice = minPrice - 2;
          }

          newTierData.reverse();
        } else {
          let previousPrice = 0;
          let index = 0;

          for (const tier of newTierData) {
            previousPrice = Math.max(index * 2, previousPrice);

            if (tier.amount) {
              if (tier.amount * 1 < previousPrice + 2) {
                tier.amount = previousPrice + 2;
                tier.shouldFlashAmount = tier.shouldFlashAmount
                  ? tier.shouldFlashAmount + 1
                  : 1;
                newErrorMessage = errorMessages.difference;
                if (tier.amount > maxAllowedPrice) {
                  setErrorMessage(errorMessages.differenceAtMost);
                  return; // abort the opperation, it is not possible to make it valid
                }
              }
              previousPrice = tier.amount * 1;
              tier.amount = (Math.round(tier.amount * 100) / 100).toFixed(2);
            }
            index++;
          }
        }
      }
      setErrorMessage(newErrorMessage);
      setTierData(newTierData);
    },
    [setErrorMessage, setTierData, tierData]
  );

  return setTier;
};

export {
  useSortedTierData,
  useMaxCostHeaderText,
  useMinTierCost,
  useMaxTierCost,
  useAddTier,
  useNonMaxTierData,
  useSuggestedDonation,
  useEntity,
  useIsDisabled,
  useSetTier,
  useMaxTierId,
};
